import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Volume2, VolumeX, X, Play, Pause, RotateCcw, RotateCw, Loader2 } from 'lucide-react';
import { videoAudio } from '../../services/videoAudioManager';
import afroYapLogo from '../../assets/afroyap_full_logo.png';

interface YapVideoPlayerProps {
  videoUrl: string;
  trimStart?: number;
  trimEnd?: number;
  /**
   * Detail view renders at full width with the full native aspect ratio (no
   * height cap). Any feed/list/forum/tribe context leaves this false (default)
   * so the video is capped at FEED_MAX_HEIGHT.
   */
  isDetailView?: boolean;
  /** Poster's display name — shown as the artist in the OS now-playing card. */
  authorName?: string;
  /**
   * Real video dimensions captured at upload time. When provided, the container
   * is sized to the correct aspect ratio on first render — no flash from the
   * 16:9 placeholder snapping to the real ratio once metadata loads.
   */
  videoWidth?: number;
  videoHeight?: number;
  className?: string;
}

// Set OS now-playing metadata (iOS lock screen / control center) so it shows
// AfroYap branding instead of the raw Firebase Storage URL when a video has audio.
function setNowPlaying(authorName?: string) {
  try {
    const ms: any = (navigator as any).mediaSession;
    if (!ms || typeof (window as any).MediaMetadata !== 'function') return;
    ms.metadata = new (window as any).MediaMetadata({
      title: 'AfroYap Video',
      artist: authorName ? authorName : 'AfroYap',
      album: 'AfroYap',
      artwork: [
        { src: afroYapLogo, sizes: '512x512', type: 'image/png' },
        { src: afroYapLogo, sizes: '256x256', type: 'image/png' },
      ],
    });
  } catch { /* mediaSession unsupported — ignore */ }
}

const FEED_MAX_HEIGHT = 280;
const FEED_PORTRAIT_HEIGHT = 268;
const FALLBACK_ASPECT = 16 / 9;

const fmtClock = (s: number) => {
  const t = Math.max(0, Math.floor(s || 0));
  const m = Math.floor(t / 60);
  const sec = t % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

/**
 * AfroYap video player — shared across YapTab feed, Yap Details, Forums, Tribes,
 * and a fully custom (no native iOS chrome / AirPlay / cast) fullscreen player.
 *
 *   • Inline: autoplays muted; speaker toggles mute (per-post, single global
 *     audio source via videoAudioManager); plays only while visible.
 *   • Fullscreen: opens at the EXACT position the feed video was at (resume),
 *     reuses the same source URL (served from browser cache, not re-fetched),
 *     shows a spinner while seeking (never a black screen), and has fully custom
 *     controls — play/pause + skip ±10s (centre, auto-fade), seek bar with
 *     elapsed/total time, mute, and a close button.
 */
export function YapVideoPlayer({
  videoUrl,
  trimStart,
  trimEnd,
  isDetailView = false,
  authorName,
  videoWidth,
  videoHeight,
  className = '',
}: YapVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fsVideoRef = useRef<HTMLVideoElement>(null);
  // Latest inline playback position — used to resume the fullscreen player.
  const lastTimeRef = useRef(0);

  const baseId = useId();
  const inlineId = baseId;
  const fsId = `${baseId}:fs`;

  const [muted, setMuted] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [fsStartTime, setFsStartTime] = useState(0);
  // Seed from stored upload dimensions when available so the container is sized
  // correctly on first paint (no aspect-ratio flash). Falls back to null → the
  // 16:9 placeholder until onLoadedMetadata for older videos with no stored dims.
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(
    videoWidth && videoHeight && videoWidth > 0 && videoHeight > 0
      ? { w: videoWidth, h: videoHeight }
      : null
  );
  const [containerWidth, setContainerWidth] = useState(0);

  // Fullscreen player UI state
  const [fsPlaying, setFsPlaying] = useState(true);
  const [fsMuted, setFsMuted] = useState(false);
  const [fsCurrent, setFsCurrent] = useState(0);
  const [fsDuration, setFsDuration] = useState(0);
  const [fsBuffering, setFsBuffering] = useState(true);
  const [fsControls, setFsControls] = useState(true);
  const controlsTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const hasTrim =
    typeof trimStart === 'number' && typeof trimEnd === 'number' && trimEnd > trimStart;
  const winStart = hasTrim ? (trimStart as number) : 0;
  const winEnd = hasTrim ? (trimEnd as number) : fsDuration;

  // ── Sizing ────────────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Global single audio source ─────────────────────────────────────────────
  useEffect(() => {
    const unsub = videoAudio.subscribe((active) => {
      if (active !== inlineId) setMuted(true);
    });
    return () => {
      unsub();
      videoAudio.clearIfActive(inlineId);
      videoAudio.clearIfActive(fsId);
    };
  }, [inlineId, fsId]);

  // ── Play only while visible; mute on scroll-off ────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    const v = videoRef.current;
    if (!el || !v) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (!fullscreen) v.play().catch(() => {});
        } else {
          v.pause();
          setMuted(true);
          videoAudio.clearIfActive(inlineId);
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [videoUrl, inlineId, fullscreen]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.videoWidth && v.videoHeight) setNaturalSize({ w: v.videoWidth, h: v.videoHeight });
    if (hasTrim) { try { v.currentTime = trimStart!; } catch (_) {} }
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    lastTimeRef.current = v.currentTime;
    if (hasTrim && v.currentTime >= (trimEnd as number)) {
      try { v.currentTime = trimStart as number; } catch (_) {}
    }
  };

  // ── Fullscreen open/close + controls ───────────────────────────────────────
  const openFullscreen = () => {
    setFsStartTime(lastTimeRef.current || winStart);
    setFsBuffering(true);
    setFsPlaying(true);
    setFsMuted(false);
    videoAudio.setActive(fsId); // mutes the inline copy via subscription
    setNowPlaying(authorName); // OS now-playing shows AfroYap, not the Storage URL
    setFullscreen(true);
    pokeControls();
  };

  const closeFullscreen = () => {
    videoAudio.clearIfActive(fsId);
    setFullscreen(false);
  };

  const pokeControls = () => {
    setFsControls(true);
    if (controlsTimer.current) clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => setFsControls(false), 2000);
  };

  useEffect(() => () => { if (controlsTimer.current) clearTimeout(controlsTimer.current); }, []);

  const fsTogglePlay = () => {
    const v = fsVideoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setFsPlaying(true); }
    else { v.pause(); setFsPlaying(false); }
    pokeControls();
  };
  const fsSkip = (delta: number) => {
    const v = fsVideoRef.current;
    if (!v) return;
    const lo = winStart;
    const hi = winEnd || v.duration || 0;
    v.currentTime = Math.min(hi, Math.max(lo, v.currentTime + delta));
    pokeControls();
  };
  const fsToggleMute = () => {
    const v = fsVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setFsMuted(v.muted);
    pokeControls();
  };
  const fsSeekTo = (t: number) => {
    const v = fsVideoRef.current;
    if (!v) return;
    v.currentTime = t;
    setFsCurrent(t);
    pokeControls();
  };

  const aspect = naturalSize ? naturalSize.w / naturalSize.h : FALLBACK_ASPECT;
  const isPortrait = aspect < 1;
  let boxStyle: CSSProperties;
  if (isDetailView) {
    boxStyle = { width: '100%', aspectRatio: `${aspect}` };
  } else if (containerWidth > 0 && naturalSize) {
    const cap = isPortrait ? FEED_PORTRAIT_HEIGHT : FEED_MAX_HEIGHT;
    const targetHeight = Math.min(containerWidth / aspect, cap);
    boxStyle = { width: targetHeight * aspect, height: targetHeight };
  } else {
    boxStyle = { width: '100%', aspectRatio: `${aspect}`, maxHeight: FEED_PORTRAIT_HEIGHT };
  }

  const dispCur = Math.max(0, fsCurrent - winStart);
  const dispDur = Math.max(0, (winEnd || fsDuration) - winStart);

  return (
    <div ref={containerRef} className={`mt-1 w-full flex justify-start ${className}`}>
      {/* Inline preview */}
      <div
        className="relative rounded-2xl overflow-hidden bg-black cursor-pointer"
        style={boxStyle}
        onClick={(e) => { e.stopPropagation(); openFullscreen(); }}
      >
        <video
          ref={videoRef}
          src={videoUrl}
          autoPlay
          muted
          playsInline
          loop={!hasTrim}
          preload="metadata"
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }}
        />
        <button
          type="button"
          aria-label={muted ? 'Unmute video' : 'Mute video'}
          onClick={(e) => {
            e.stopPropagation();
            setMuted((m) => {
              const next = !m;
              if (!next) { videoAudio.setActive(inlineId); setNowPlaying(authorName); }
              else videoAudio.clearIfActive(inlineId);
              return next;
            });
          }}
          className="absolute bottom-2 right-2 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>

      {/* ── Custom AfroYap fullscreen player (no native controls) ── */}
      {fullscreen && createPortal(
        <div
          className="fixed inset-0 bg-black flex items-center justify-center select-none"
          style={{ zIndex: 2147483647 }}
          onClick={pokeControls}
        >
          <video
            ref={fsVideoRef}
            src={videoUrl}
            autoPlay
            playsInline
            preload="auto"
            // NOTE: no `controls` → no AirPlay/cast/system chrome.
            disablePictureInPicture
            {...({ 'x-webkit-airplay': 'deny' } as any)}
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              setFsDuration(v.duration || 0);
              v.muted = false;
              setFsMuted(false);
              // Resume at the exact feed position (clamped into the trim window).
              const target = Math.min(
                hasTrim ? (trimEnd as number) : (v.duration || fsStartTime),
                Math.max(hasTrim ? (trimStart as number) : 0, fsStartTime)
              );
              try { v.currentTime = target; } catch (_) {}
              v.play().then(() => setFsPlaying(true)).catch(() => setFsPlaying(false));
            }}
            onSeeked={() => setFsBuffering(false)}
            onCanPlay={() => setFsBuffering(false)}
            onPlaying={() => { setFsBuffering(false); setFsPlaying(true); }}
            onWaiting={() => setFsBuffering(true)}
            onPlay={() => setFsPlaying(true)}
            onPause={() => setFsPlaying(false)}
            onTimeUpdate={(e) => {
              const v = e.currentTarget;
              setFsCurrent(v.currentTime);
              if (hasTrim && v.currentTime >= (trimEnd as number)) {
                try { v.currentTime = trimStart as number; } catch (_) {}
              }
            }}
            onEnded={() => { if (!hasTrim) setFsPlaying(false); }}
            className="object-contain"
            style={{ width: '100vw', height: '100vh', background: '#000' }}
          />

          {/* Buffering / seeking spinner */}
          {fsBuffering && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Loader2 className="w-10 h-10 text-white/80 animate-spin" />
            </div>
          )}

          {/* Controls overlay (auto-fades) */}
          <div
            className={`absolute inset-0 transition-opacity duration-300 ${fsControls || !fsPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              type="button"
              aria-label="Close video"
              onClick={(e) => { e.stopPropagation(); closeFullscreen(); }}
              className="absolute right-4 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-black/55 text-white backdrop-blur-sm hover:bg-black/80"
              style={{ top: 'max(16px, env(safe-area-inset-top))' }}
            >
              <X className="w-6 h-6" />
            </button>

            {/* Centre transport: skip back / play-pause / skip forward */}
            {!fsBuffering && (
              <div className="absolute inset-0 flex items-center justify-center gap-8">
                <button type="button" aria-label="Back 10 seconds" onClick={(e) => { e.stopPropagation(); fsSkip(-10); }}
                  className="flex items-center justify-center w-12 h-12 rounded-full bg-black/45 text-white backdrop-blur-sm active:scale-90 transition-transform">
                  <RotateCcw className="w-6 h-6" />
                </button>
                <button type="button" aria-label={fsPlaying ? 'Pause' : 'Play'} onClick={(e) => { e.stopPropagation(); fsTogglePlay(); }}
                  className="flex items-center justify-center w-16 h-16 rounded-full bg-white/15 text-white backdrop-blur-md active:scale-90 transition-transform">
                  {fsPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-0.5" />}
                </button>
                <button type="button" aria-label="Forward 10 seconds" onClick={(e) => { e.stopPropagation(); fsSkip(10); }}
                  className="flex items-center justify-center w-12 h-12 rounded-full bg-black/45 text-white backdrop-blur-sm active:scale-90 transition-transform">
                  <RotateCw className="w-6 h-6" />
                </button>
              </div>
            )}

            {/* Bottom bar: seek + time + mute — kept clear of the home indicator. */}
            <div
              className="absolute left-0 right-0 px-4 pt-10"
              style={{ bottom: 0, paddingBottom: 'max(32px, calc(env(safe-area-inset-bottom) + 20px))', background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' }}
            >
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-white/90 font-mono tabular-nums shrink-0">{fmtClock(dispCur)}</span>
                <input
                  type="range"
                  min={winStart}
                  max={(winEnd || fsDuration) || 0}
                  step={0.1}
                  value={fsCurrent}
                  onChange={(e) => fsSeekTo(parseFloat(e.target.value))}
                  className="flex-1 h-1 accent-[#4478ea] cursor-pointer"
                />
                <span className="text-[11px] text-white/90 font-mono tabular-nums shrink-0">{fmtClock(dispDur)}</span>
                <button type="button" aria-label={fsMuted ? 'Unmute' : 'Mute'} onClick={(e) => { e.stopPropagation(); fsToggleMute(); }}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-black/45 text-white backdrop-blur-sm shrink-0">
                  {fsMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default YapVideoPlayer;
